import { useCallback, useEffect, useState } from "react";
import {
  workLocationsAutoCompleteQuery,
  workLocationsByCustomerCompanyId,
} from "../../../../../features/customer/customerAPI";
import { WorkLocation } from "../../../../../features/customer/types/queries";
import { useAppDispatch } from "../../../../../app/hooks";
import { fetchWorkLocationsAndContractsByOrgId } from "../../../../../features/customer/customerWorkLocationsSlice";
import { isFulfilled } from "@reduxjs/toolkit";

export interface WorkLocationOption {
  id: string;
  name: string;
  customerOrgId: string;
  customerOrgName: string;
  active: boolean;
  contractId: string;
}

type Props = {
  customerCompanyId?: string;
  customerOrgId?: string;
  contractId?: string;
  useFixedOptions?: boolean;
};

/**
 * Hook for autocompleting work locations with flexible filtering options.
 *
 * Features:
 * - Automatically resets search when customerCompanyId or customerOrgId changes
 * - Results are sorted by customerOrgName, then by name (Finnish locale)
 * - Two search modes:
 *   1. Dynamic search by search term (default)
 *   2. Fixed option search (fetches all work locations for given org)
 *
 * Usage examples:
 *
 * 1. Search ALL work locations:
 *    useWorkLocationAutocomplete()
 *    - No parameters needed
 *    - Backend searches from all work locations
 *
 * 2. Search work locations for a specific customer company:
 *    useWorkLocationAutocomplete({ customerCompanyId: "ccom123456" })
 *    - Backend filters by customerCompanyId (optimized query)
 *    - Returns all work locations belonging to that company's organizations
 *
 * 3. Search work locations for a specific customer organization:
 *    useWorkLocationAutocomplete({
 *      customerCompanyId: "ccom123456",
 *      customerOrgId: "corg123456"
 *    })
 *    - Backend filters by customerCompanyId (optimized query)
 *    - Frontend filters by customerOrgId (precise results)
 *    - Returns only work locations belonging to that specific organization
 *
 * 4. Fixed option search for a specific customer organization:
 *    useWorkLocationAutocomplete({
 *     customerOrgId: "corg123456",
 *     useFixedOptions: true
 *    })
 *    - Backend fetches ALL work locations for the given organization
 *    - Dynamic search term is not used
 *    - Useful for scenarios where user should select from all options of an organization
 *
 */
const useWorkLocationAutocomplete = ({
  customerCompanyId,
  customerOrgId,
  contractId,
  useFixedOptions = false,
}: Props = {}) => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [workLocationOptions, setWorkLocationOptions] = useState<
    WorkLocationOption[]
  >([]);

  const formatWorkLocation = (wl: WorkLocation): WorkLocationOption => ({
    id: wl.id,
    name: wl.name,
    customerOrgId: wl.customerOrgId,
    customerOrgName: wl.customerOrgName || "",
    active: wl.active,
    contractId: wl.contractId,
  });

  const handleWorkLocationsResponse = useCallback(
    (workLocations: WorkLocation[]) => {
      let options: WorkLocationOption[] = workLocations.map(
        (wl: WorkLocation) => formatWorkLocation(wl),
      );

      if (customerOrgId) {
        options = options.filter((wl) => wl.customerOrgId === customerOrgId);
      }

      if (contractId) {
        options = options.filter((wl) => wl.contractId === contractId);
      }

      const sortedOptions = options.sort((a, b) => {
        const orgCompare = a.customerOrgName.localeCompare(
          b.customerOrgName,
          "fi",
        );
        if (orgCompare !== 0) return orgCompare;
        return a.name.localeCompare(b.name, "fi");
      });

      setWorkLocationOptions(sortedOptions);
    },
    [customerOrgId, contractId],
  );

  // If fixed option search mode is on, don't use dynamic search,
  // just fetch all work locations for the org
  useEffect(() => {
    if (!useFixedOptions || !customerOrgId) return;
    // TODO implement query that fetches only work locations by org ID
    dispatch(fetchWorkLocationsAndContractsByOrgId({ id: customerOrgId })).then(
      (action) => {
        if (isFulfilled(action)) {
          let options: WorkLocationOption[] = action.payload.workLocations.map(
            (wl: WorkLocation) => formatWorkLocation(wl),
          );

          if (contractId) {
            options = options.filter((wl) => wl.contractId === contractId);
          }

          const sortedOptions = options.sort((a, b) =>
            a.name.localeCompare(b.name, "fi"),
          );

          setWorkLocationOptions(sortedOptions);
        }
      },
    );
  }, [useFixedOptions, customerOrgId, contractId, dispatch]);

  useEffect(() => {
    if (useFixedOptions || (!useFixedOptions && !customerCompanyId)) return;
    if (searchTerm) {
      workLocationsAutoCompleteQuery({
        term: searchTerm,
        customerCompanyId,
      }).then((res) => handleWorkLocationsResponse(res));
    } else {
      if (customerCompanyId) {
        workLocationsByCustomerCompanyId({
          customerCompanyId,
        }).then((res) => handleWorkLocationsResponse(res));
      } else {
        setWorkLocationOptions([]);
      }
    }
  }, [
    searchTerm,
    customerCompanyId,
    customerOrgId,
    useFixedOptions,
    handleWorkLocationsResponse,
    dispatch,
  ]);

  // Auto-reset when filtering criteria changes
  useEffect(() => {
    setSearchTerm("");
    setWorkLocationOptions([]);
  }, [customerCompanyId, customerOrgId, contractId]);

  return {
    setSearchTerm,
    workLocationOptions,
  };
};

export default useWorkLocationAutocomplete;
